import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Alert, Box, Button, Grid, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { track } from 'src/configs/amplitude';
import { answered, succeeded } from './asyncResult';
import { accountNumberError, credentialSaveErrorMessage } from './credentialSaveError';
import { PAUSED_STAFF_MESSAGE, isPausedCredential } from './credentialStatus';
const EMPTY = {
  username: '',
  password: '',
  accountReference: '',
  serviceAddress: '',
  instructions: ''
};
const NUMBER_REQUIRED = 'Enter the utility account number for this login.';

/** The account number field's line: its error first, then why it's asked for or why it can't change. */
const accountNumberHelp = _ref => {
  let accountError = _ref.accountError,
    addsNumber = _ref.addsNumber,
    isEdit = _ref.isEdit,
    numbersRequired = _ref.numbersRequired;
  if (accountError) return accountError;
  if (addsNumber) return 'This login has no account number yet. Add it to save any change.';
  if (isEdit) return 'To move this login to a different account, contact Livable support.';
  return numbersRequired ? 'The account number on the utility bill.' : undefined;
};

/** Dots in an empty field that holds a saved secret, dark enough to read as a value, never the value itself. */
const SAVED_DOTS = '••••••••••';
const savedPlaceholder = {
  '& input::placeholder': {
    opacity: 1,
    color: 'text.primary',
    letterSpacing: '2px'
  }
};
const fromExisting = existing => _objectSpread(_objectSpread({}, EMPTY), {}, {
  accountReference: (existing === null || existing === void 0 ? void 0 : existing.accountReference) || '',
  serviceAddress: (existing === null || existing === void 0 ? void 0 : existing.serviceAddress) || '',
  instructions: (existing === null || existing === void 0 ? void 0 : existing.instructions) || ''
});

/**
 * Add a utility portal login, or edit the one already saved for this property utility.
 *
 * Editing updates that login by id. A blank username or password keeps the saved one, and a save that
 * changes only the address or instructions leaves the secret and its status alone.
 *
 * The password is WRITE-ONLY. It is never populated from a response, never rendered back, and there
 * is deliberately no reveal control - the authenticated API does not return the secret, and the UI
 * must not invent a way to ask for it. The field is cleared as soon as the save succeeds so the
 * secret does not sit in component state for the life of the page.
 */
export const CredentialForm = observer(_ref2 => {
  var _store$account, _store$admin$saveUtil;
  let companyId = _ref2.companyId,
    utilityProviderId = _ref2.utilityProviderId,
    propertyId = _ref2.propertyId,
    propertyUtilityId = _ref2.propertyUtilityId,
    utilityTypeId = _ref2.utilityTypeId,
    propertyReference = _ref2.propertyReference,
    existing = _ref2.existing,
    propertyAddress = _ref2.propertyAddress,
    onResume = _ref2.onResume,
    onSaved = _ref2.onSaved;
  const store = useStore();
  const isEdit = !!existing;
  // With billing options on, a login serves the account it names (LB-6184): a new one needs its number, and one saved
  // without a number gets it with its next edit. A number already saved stays: moving it re-routes bills.
  const numbersRequired = !!((_store$account = store.account) !== null && _store$account !== void 0 && (_store$account = _store$account.featureFlagsStore) !== null && _store$account !== void 0 && _store$account.getIsBillingOptionsEnabled);
  const addsNumber = isEdit && numbersRequired && existing.needsAccountNumber === true;
  const asksNumber = numbersRequired && (!isEdit || addsNumber);
  const _useState = useState(() => fromExisting(existing)),
    _useState2 = _slicedToArray(_useState, 2),
    form = _useState2[0],
    setForm = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    error = _useState4[0],
    setError = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    accountError = _useState6[0],
    setAccountError = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    saving = _useState8[0],
    setSaving = _useState8[1];
  const _useState9 = useState(''),
    _useState0 = _slicedToArray(_useState9, 2),
    notice = _useState0[0],
    setNotice = _useState0[1];
  const paused = isPausedCredential(existing);
  // With billing options on, a page can hold a login form per utility under its setup (LB-6191), all saving through
  // the one store model, whose next run cancels the one in flight. So each holds its Save while another one saves.
  const anotherSaving = numbersRequired && !saving && !!((_store$admin$saveUtil = store.admin.saveUtilityCredentialAsync) !== null && _store$admin$saveUtil !== void 0 && _store$admin$saveUtil.inProgress);
  const touched = useRef(new Set());
  // Bumped per save and on unmount, so a completion from a closed or superseded form changes nothing.
  const submission = useRef(0);
  useEffect(() => () => void (submission.current += 1), []);

  // Same reason as the section above: under saas-react-components v3 the modal keeps its children
  // mounted, so saved values that arrive after this mounts (or a refresh after the last save) are not
  // picked up by a remount. Follows the saved values for every field the user has not edited, so it
  // never overwrites what someone is typing and never resends a stale value.
  const savedAccount = (existing === null || existing === void 0 ? void 0 : existing.accountReference) || '';
  const savedAddress = (existing === null || existing === void 0 ? void 0 : existing.serviceAddress) || '';
  const savedInstructions = (existing === null || existing === void 0 ? void 0 : existing.instructions) || '';
  useEffect(() => {
    const saved = {
      accountReference: savedAccount,
      serviceAddress: savedAddress,
      instructions: savedInstructions
    };
    setForm(current => {
      const next = _objectSpread({}, current);
      Object.keys(saved).forEach(field => {
        if (!touched.current.has(field)) next[field] = saved[field];
      });
      return next;
    });
  }, [savedAccount, savedAddress, savedInstructions]);
  const createChangeHandler = field => event => {
    touched.current.add(field);
    if (field === 'accountReference') setAccountError('');
    setForm(current => _objectSpread(_objectSpread({}, current), {}, {
      [field]: event.target.value
    }));
  };
  const buildBody = () => {
    const scope = {
      companyId,
      utilityProviderId,
      propertyId,
      propertyUtilityId,
      utilityTypeId
    };
    if (isEdit) {
      return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, scope), {}, {
        credentialId: existing.id,
        // Sent as typed: an emptied field clears the saved value, an untouched one resends it.
        serviceAddress: form.serviceAddress,
        instructions: form.instructions
      }, addsNumber ? {
        accountReference: form.accountReference.trim()
      } : {}), form.username.trim() ? {
        username: form.username.trim()
      } : {}), form.password ? {
        password: form.password
      } : {});
    }
    return _objectSpread(_objectSpread({}, scope), {}, {
      propertyReference,
      accountReference: form.accountReference.trim() || undefined,
      serviceAddress: form.serviceAddress.trim() || undefined,
      instructions: form.instructions.trim() || undefined,
      username: form.username.trim(),
      password: form.password
    });
  };
  const validate = () => {
    // An edit names the login by id, so only a new login needs the provider.
    if (!companyId || !isEdit && !utilityProviderId) {
      return 'This utility is missing a company or provider, so a login cannot be saved against it.';
    }
    if (!isEdit) {
      return !form.username.trim() || !form.password ? 'Enter the portal username and password.' : '';
    }
    const unchanged = !form.username.trim() && !form.password && !(addsNumber && form.accountReference.trim()) && form.serviceAddress === (existing.serviceAddress || '') && form.instructions === (existing.instructions || '');
    return unchanged ? 'Nothing has changed yet.' : '';
  };
  const handleSubmit = async () => {
    if (anotherSaving) return;
    const invalid = validate();
    const missingNumber = asksNumber && !form.accountReference.trim();
    setError(invalid);
    setAccountError(missingNumber ? NUMBER_REQUIRED : '');
    if (invalid || missingNumber) return;
    const model = store.admin.saveUtilityCredentialAsync;
    const rotated = !isEdit || !!(form.username.trim() || form.password);
    const tracked = ['username', 'password', 'serviceAddress', 'instructions', ...(addsNumber ? ['accountReference'] : [])];
    const fields = tracked.filter(f => form[f].trim());
    const mine = ++submission.current;
    setError('');
    setSaving(true);
    try {
      // Silent: the inline message below explains the refusal, so the global toast would repeat it.
      const resp = await model.run(buildBody(), undefined, {
        silent: true
      });
      // The shared model cancels a run when another starts; a cancel resolves undefined without isError.
      if (mine !== submission.current || !model.isError && !answered(resp)) return;
      if (!succeeded(model, resp)) {
        throw model.error;
      }
      track('portal-login-saved', {
        mode: isEdit ? 'edit' : 'add',
        fields
      });
      // Drop the secret from state the moment it is no longer needed.
      setForm(current => _objectSpread(_objectSpread({}, current), {}, {
        username: '',
        password: ''
      }));
      // A new password resumes a paused login on the server; details alone leave it paused, so say so.
      setNotice(paused && !rotated ? 'Saved. The login is still paused; resume it to try again.' : '');
      if (onSaved) await onSaved({
        rotated
      });
    } catch (ex) {
      var _model$error$status, _model$error;
      if (mine !== submission.current) return;
      track('portal-login-save-refused', {
        mode: isEdit ? 'edit' : 'add',
        fields,
        status: (_model$error$status = (_model$error = model.error) === null || _model$error === void 0 ? void 0 : _model$error.status) !== null && _model$error$status !== void 0 ? _model$error$status : null
      });
      // A refusal about the account number shows on its field.
      const onNumber = accountNumberError(model.error);
      setAccountError(onNumber !== null && onNumber !== void 0 ? onNumber : '');
      setError(onNumber ? '' : credentialSaveErrorMessage(model.error));
    } finally {
      if (mine === submission.current) setSaving(false);
    }
  };
  return /*#__PURE__*/React.createElement(Box, null, paused && onResume ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning",
    sx: {
      marginBottom: '16px'
    },
    action: /*#__PURE__*/React.createElement(Button, {
      color: "inherit",
      size: "small",
      onClick: onResume
    }, "Resume")
  }, PAUSED_STAFF_MESSAGE) : null, notice ? /*#__PURE__*/React.createElement(Alert, {
    severity: "info",
    sx: {
      marginBottom: '16px'
    }
  }, notice) : null, isEdit ? /*#__PURE__*/React.createElement(Alert, {
    severity: "info",
    sx: {
      marginBottom: '16px'
    }
  }, "The login is saved. Type in a field only to change it; anything left as is stays as saved.") : /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      marginBottom: '16px'
    }
  }, "Logins are encrypted before they're stored and are never shown again. Only a masked username appears after saving."), error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      marginBottom: '16px'
    }
  }, error) : null, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 20
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    sm: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    label: "Portal username",
    value: form.username,
    onChange: createChangeHandler('username'),
    autoComplete: "off",
    placeholder: isEdit ? existing.usernameHint || SAVED_DOTS : undefined,
    InputLabelProps: isEdit ? {
      shrink: true
    } : undefined,
    sx: isEdit ? savedPlaceholder : undefined,
    helperText: isEdit ? 'Saved. Type to replace it.' : undefined,
    inputProps: {
      'data-testid': 'portal-username'
    }
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    sm: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    type: "password",
    label: "Portal password",
    value: form.password,
    onChange: createChangeHandler('password')
    // new-password, not current-password: stops the browser offering to fill a Livable
    // login into a third-party portal field.
    ,
    autoComplete: "new-password",
    placeholder: isEdit ? SAVED_DOTS : undefined,
    InputLabelProps: isEdit ? {
      shrink: true
    } : undefined,
    sx: isEdit ? savedPlaceholder : undefined,
    helperText: isEdit ? 'Saved. Type to replace it.' : undefined,
    inputProps: {
      'data-testid': 'portal-password'
    }
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    sm: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    label: isEdit || numbersRequired ? 'Account number' : 'Account number (optional)',
    value: form.accountReference,
    onChange: createChangeHandler('accountReference'),
    disabled: isEdit && !addsNumber,
    required: asksNumber,
    error: !!accountError,
    helperText: accountNumberHelp({
      accountError,
      addsNumber,
      isEdit,
      numbersRequired
    }),
    inputProps: {
      'data-testid': 'portal-account'
    }
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    sm: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    label: "Service address (optional)",
    value: form.serviceAddress,
    onChange: createChangeHandler('serviceAddress'),
    helperText: isEdit && !form.serviceAddress && propertyAddress ? "Not saved yet. The property is at ".concat(propertyAddress, ".") : undefined,
    inputProps: {
      'data-testid': 'portal-address'
    }
  }), isEdit && !form.serviceAddress && propertyAddress ? /*#__PURE__*/React.createElement(Button, {
    size: "small",
    variant: "actionButtonInvert",
    sx: {
      marginTop: '6px'
    },
    onClick: () => createChangeHandler('serviceAddress')({
      target: {
        value: propertyAddress
      }
    })
  }, "Use property address") : null), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    multiline: true,
    minRows: 2,
    label: "Login instructions (optional)",
    placeholder: "None saved. Add security questions, or which account to pick.",
    InputLabelProps: {
      shrink: true
    },
    value: form.instructions,
    onChange: createChangeHandler('instructions'),
    inputProps: {
      'data-testid': 'portal-instructions'
    }
  }))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      marginTop: '20px',
      display: 'flex',
      justifyContent: 'flex-end'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    disabled: saving || anotherSaving,
    onClick: handleSubmit
  }, saving ? 'Saving...' : isEdit ? 'Save changes' : 'Save login')));
});